import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Collapsible from 'src/components/Collapsible/Collapsible'
import NotificationSettingsGroupItem from 'src/components/NotificationSettingsGroupItem/NotificationSettingsGroupItem'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import { useTranslate } from 'src/localization/Trns'
import { NotificationSettingListModel } from 'src/models/notificationSetting.models'
import { selectIsCspAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'
import { PatchNotificationSettingsRequest } from 'src/requests/patchNotificationSettings.request'
import { getReactNodeSourceString } from 'src/utils/reactNode.utils'
import { css } from 'styled-components'

export interface NotificationSettingsGroup {
  label: React.ReactNode
  analyticsLabel?: string
  cardholderSettings?: Record<
    string,
    {
      email?: NotificationSettingListModel // TODO: put this kind of stuff in enums
      push?: NotificationSettingListModel
    }
  >
  recipientSettings?: Record<
    string,
    {
      email?: NotificationSettingListModel
      push?: NotificationSettingListModel
    }
  >
  emailSelected: number
  emailTotal: number
  pushSelected: number
  pushTotal: number
  stringLabel?: string
}

interface NotificationSettingsGroupEntryProps {
  className?: string
  notificationSettingGroup: NotificationSettingsGroup
  patchNotifications: (body: PatchNotificationSettingsRequest) => void
}

const NotificationSettingsGroupEntry: React.FC<
  NotificationSettingsGroupEntryProps
> = (props): React.ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const { analytics } = useAnalyticsContext()
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const translate = useTranslate()
  const notificationLabel =
    props.notificationSettingGroup.analyticsLabel ??
    getReactNodeSourceString(props.notificationSettingGroup.label) ??
    ''

  const handleTurnOnAllNotif = (
    label: string,
    notificationType: string,
  ): void => {
    analytics?.track(
      'notifications ' +
        label +
        ' - all ' +
        notificationType +
        ' - checkbox press',
      {
        screen: '/notifications',
        interaction: 'check press',
        intent: notificationType + ' all ' + label + ' notifications',
      },
    )
  }

  const handleCancelAllNotif = (
    label: string,
    notificationType: string,
  ): void => {
    analytics?.track(
      'notifications ' +
        label +
        ' - cancel all ' +
        notificationType +
        ' - checkbox press',
      {
        screen: '/notifications',
        interaction: 'check press',
        intent:
          'cancel ' + notificationType + ' all ' + label + ' notifications',
      },
    )
  }

  const onGroupClick = (
    group: NotificationSettingsGroup,
    channel: 'email' | 'push',
  ): void => {
    const groupEmailSettings = [
      ...Object.values(group.cardholderSettings || {}).reduce(
        (acc: NotificationSettingListModel[], curr) => {
          const setting = curr[channel]
          return setting !== undefined ? [...acc, setting] : acc
        },
        [],
      ),
      ...Object.values(group.recipientSettings || {}).reduce(
        (acc: NotificationSettingListModel[], curr) => {
          const setting = curr[channel]
          return setting !== undefined ? [...acc, setting] : acc
        },
        [],
      ),
    ]

    props.patchNotifications({
      notificationSettings: groupEmailSettings.map((setting) => ({
        id: setting.id,
        enabled:
          channel === 'email'
            ? group.emailSelected < group.emailTotal
            : group.pushSelected < group.pushTotal,
      })),
    })
  }

  const onClick = (): void => {
    setIsOpen(!isOpen)
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        &:nth-child(n + 2) {
          border-top: 1px solid ${styleConstants.colors.gray_light};
        }
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 0px;
        `}
      >
        <span
          onClick={(): void => {
            analytics?.track(
              isOpen
                ? 'notification settings - close ' +
                    notificationLabel +
                    ' - button press'
                : 'notification settings - open ' +
                    notificationLabel +
                    ' - button press',
              {
                intent: 'view settings',
                interactions: 'button press',
              },
            )
          }}
          onKeyPress={(): null => {
            return null
          }}
          role="button"
          tabIndex={0}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              flex-grow: 2;
              cursor: pointer;
            `}
            onClick={onClick}
            onKeyPress={onClick}
            role="button"
            tabIndex={0}
          >
            {isOpen && <SvgChevronUp />}
            {!isOpen && <SvgChevronDown />}
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-left: 10px;
              `}
            >
              {props.notificationSettingGroup.label}
            </span>
          </div>
        </span>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 200px;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: ${isCspAuthenticated ? 'not-allowed' : 'pointer'};
              ${props.notificationSettingGroup.emailTotal === 0 &&
              css`
                opacity: 0;
                pointer-events: none;
              `}
            `}
          >
            <Checkbox
              id={`email-${props.notificationSettingGroup?.stringLabel}`}
              data-testid={props.notificationSettingGroup?.stringLabel}
              onChange={(): void => {
                onGroupClick(props.notificationSettingGroup, 'email')
                props.notificationSettingGroup.emailSelected ===
                props.notificationSettingGroup.emailTotal
                  ? handleCancelAllNotif(notificationLabel, 'email')
                  : handleTurnOnAllNotif(notificationLabel, 'email')
              }}
              checked={props.notificationSettingGroup.emailSelected > 0}
              isPartial={
                props.notificationSettingGroup.emailSelected > 0 &&
                props.notificationSettingGroup.emailSelected <
                  props.notificationSettingGroup.emailTotal
              }
              disabled={isCspAuthenticated}
              aria-label={translate('Email')}
            />
            <label
              htmlFor={`email-${props.notificationSettingGroup?.stringLabel}`}
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                cursor: ${isCspAuthenticated ? 'not-allowed' : 'pointer'};
              `}
            >
              {translate('Email')}
            </label>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: ${isCspAuthenticated ? 'not-allowed' : 'pointer'};
              margin-left: 30px;
              ${props.notificationSettingGroup.pushTotal === 0 &&
              css`
                opacity: 0;
                pointer-events: none;
              `}
            `}
          >
            <Checkbox
              id={`push-${props.notificationSettingGroup?.stringLabel}`}
              onChange={(): void => {
                onGroupClick(props.notificationSettingGroup, 'push')
                props.notificationSettingGroup.pushSelected ===
                props.notificationSettingGroup.pushTotal
                  ? handleCancelAllNotif(notificationLabel, 'push')
                  : handleTurnOnAllNotif(notificationLabel, 'push')
              }}
              checked={props.notificationSettingGroup.pushSelected > 0}
              isPartial={
                props.notificationSettingGroup.pushSelected > 0 &&
                props.notificationSettingGroup.pushSelected <
                  props.notificationSettingGroup.pushTotal
              }
              disabled={isCspAuthenticated}
              aria-label={translate('Push')}
            />
            <label
              htmlFor={`push-${props.notificationSettingGroup?.stringLabel}`}
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                cursor: ${isCspAuthenticated ? 'not-allowed' : 'pointer'};
              `}
            >
              {translate('Push')}
            </label>
          </div>
        </div>
      </div>
      <Collapsible isOpen={isOpen}>
        <div
          css={css`
            padding: 0px 0px 24px 60px;
            display: flex;
            flex-direction: column;
          `}
        >
          {/* TODO: extract these one more to a component */}
          {props.notificationSettingGroup.cardholderSettings !== undefined && (
            <NotificationSettingsGroupItem
              patchNotifications={props.patchNotifications}
              settings={Object.values(
                props.notificationSettingGroup.cardholderSettings,
              )}
              title={translate('Created')}
              label={notificationLabel}
            />
          )}
          {props.notificationSettingGroup.recipientSettings !== undefined && (
            <NotificationSettingsGroupItem
              patchNotifications={props.patchNotifications}
              settings={Object.values(
                props.notificationSettingGroup.recipientSettings,
              )}
              title={translate('Received')}
              label={notificationLabel}
            />
          )}
        </div>
      </Collapsible>
    </div>
  )
}

export default NotificationSettingsGroupEntry
